import React, { Fragment, useMemo, useState } from "react";
import { NavLink as ReactRouterLink } from "react-router-dom";

import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Badge, Box, Button, Checkbox, Flex, Link, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useColorModeValue } from "@chakra-ui/react";
import { EXPENSE_CATEGORIES, formatAmount, getUsedExpenseCategoriesFromRows, toFiniteNumber } from "../../utils/financialStatus";
import { hasActiveTenant } from "../../utils/financialStatusOverview";

const STATUS_LABELS = {
	empty: "Empty",
	transferred: "Received & finalized",
	received: "Received, not finalized",
	unpaid: "Not received",
};

function Money({ value, emphasize = false }) {
	const muted = useColorModeValue("gray.400", "gray.600");
	const amount = toFiniteNumber(value);
	return (
		<Text as="span" color={amount === 0 ? muted : amount < 0 ? "red.400" : undefined} fontWeight={emphasize ? "semibold" : undefined}>
			{formatAmount(amount)}
		</Text>
	);
}

function statusText(row) {
	return STATUS_LABELS[row.status.key] || row.status.key;
}

export default function FinancialStatusOverviewTable({ owners = [], query = "" }) {
	const [expandedOwners, setExpandedOwners] = useState(() => new Set());
	const [showEmptyCategories, setShowEmptyCategories] = useState(false);
	const [showEmptyProperties, setShowEmptyProperties] = useState(true);

	const headerBg = useColorModeValue("white", "gray.800");
	const ownerBg = useColorModeValue("white", "gray.800");
	const propertyBg = useColorModeValue("white", "gray.800");
	const stripeBg = useColorModeValue("gray.50", "whiteAlpha.50");
	const borderColor = useColorModeValue("gray.200", "gray.600");
	const muted = useColorModeValue("gray.500", "gray.400");

	const filteredOwners = useMemo(() => {
		const normalized = String(query || "")
			.trim()
			.toLocaleLowerCase("ro");
		if (!normalized) return owners;
		return owners.filter((entry) =>
			String(entry.owner?.name || "")
				.toLocaleLowerCase("ro")
				.includes(normalized),
		);
	}, [owners, query]);

	const usedCategories = useMemo(() => getUsedExpenseCategoriesFromRows(owners.flatMap((entry) => entry.rows || [])), [owners]);
	const categories = showEmptyCategories ? EXPENSE_CATEGORIES : usedCategories;
	const hiddenCategoryCount = EXPENSE_CATEGORIES.length - usedCategories.length;

	const toggleOwner = (ownerId) => {
		setExpandedOwners((previous) => {
			const next = new Set(previous);
			if (next.has(ownerId)) next.delete(ownerId);
			else next.add(ownerId);
			return next;
		});
	};

	const expandAll = () => setExpandedOwners(new Set(filteredOwners.map((entry) => entry.ownerId)));
	const collapseAll = () => setExpandedOwners(new Set());
	const allExpanded = filteredOwners.length > 0 && filteredOwners.every((entry) => expandedOwners.has(entry.ownerId));

	const stickyOwner = { position: "sticky", left: 0, zIndex: 1 };
	const stickyAddress = { position: "sticky", left: "13rem", zIndex: 1 };

	const renderMoneyHeaders = (key) =>
		["eur", "ron"].map((currency) => (
			<Th key={`${key}-${currency}`} isNumeric bg={headerBg}>
				{currency.toUpperCase()}
			</Th>
		));

	return (
		<Box>
			<Flex mb={3} gap={4} alignItems="center" flexWrap="wrap">
				<Button size="xs" onClick={allExpanded ? collapseAll : expandAll}>
					{allExpanded ? "Collapse all owners" : "Expand all owners"}
				</Button>
				<Checkbox size="sm" isChecked={showEmptyProperties} onChange={(event) => setShowEmptyProperties(event.target.checked)}>
					Show vacant properties
				</Checkbox>
				<Checkbox size="sm" isChecked={showEmptyCategories} onChange={(event) => setShowEmptyCategories(event.target.checked)}>
					Show empty categories
					{hiddenCategoryCount > 0 && !showEmptyCategories && (
						<Text as="span" color={muted} ml={1}>
							({hiddenCategoryCount} hidden)
						</Text>
					)}
				</Checkbox>
				<Text fontSize="xs" color={muted} ml="auto">
					{filteredOwners.length} owners
				</Text>
			</Flex>

			<TableContainer maxH="70vh" overflowY="auto" borderWidth="1px" borderRadius="md">
				<Table size="sm">
					<Thead position="sticky" top={0} zIndex={2}>
						<Tr>
							<Th {...stickyOwner} bg={headerBg}>
								Owner
							</Th>
							<Th {...stickyAddress} bg={headerBg}>
								Address
							</Th>
							<Th colSpan={2} isNumeric bg={headerBg}>
								Rent Received
							</Th>
							<Th bg={headerBg}>Method</Th>
							{categories.map((category) => (
								<Th key={category.key} colSpan={2} isNumeric bg={headerBg}>
									{category.label}
								</Th>
							))}
							<Th colSpan={2} isNumeric bg={headerBg}>
								Sub-Total
							</Th>
							<Th bg={headerBg}>Status</Th>
						</Tr>
						<Tr>
							<Th {...stickyOwner} bg={headerBg} />
							<Th {...stickyAddress} bg={headerBg} />
							{renderMoneyHeaders("rent")}
							<Th bg={headerBg} />
							{categories.flatMap((category) => renderMoneyHeaders(category.key))}
							{renderMoneyHeaders("subtotal")}
							<Th bg={headerBg} />
						</Tr>
					</Thead>
					<Tbody>
						{filteredOwners.map((entry) => {
							const expanded = expandedOwners.has(entry.ownerId);
							const visibleRows = showEmptyProperties ? entry.rows : entry.rows.filter((row) => hasActiveTenant(row.property));

							return (
								<Fragment key={entry.ownerId}>
									<Tr bg={ownerBg} fontWeight="semibold" borderTopWidth="1px" borderBottomWidth="1px" borderColor={borderColor}>
										<Td {...stickyOwner} bg={ownerBg}>
											<Flex alignItems="center" gap={2}>
												<Box
													as={expanded ? ChevronDownIcon : ChevronRightIcon}
													aria-label={expanded ? "Collapse owner" : "Expand owner"}
													boxSize={4}
													cursor="pointer"
													onClick={() => toggleOwner(entry.ownerId)}
												/>
												<Link as={ReactRouterLink} to={`/owner/view/${entry.owner?.id}`} color="inherit">
													{entry.owner?.name || "-"}
												</Link>
											</Flex>
										</Td>
										<Td {...stickyAddress} bg={ownerBg}>
											<Text fontSize="xs" color={muted} fontWeight="normal">
												{entry.rows.length} {entry.rows.length === 1 ? "property" : "properties"}
											</Text>
										</Td>
										{["eur", "ron"].map((currency) => (
											<Td key={`rent-${currency}`} isNumeric>
												<Money value={entry.totals.rent[currency]} emphasize />
											</Td>
										))}
										<Td />
										{categories.flatMap((category) =>
											["eur", "ron"].map((currency) => (
												<Td key={`${category.key}-${currency}`} isNumeric>
													<Money value={entry.totals[category.key][currency]} />
												</Td>
											)),
										)}
										{["eur", "ron"].map((currency) => (
											<Td key={`net-${currency}`} isNumeric>
												<Money value={entry.totals.net[currency]} emphasize />
											</Td>
										))}
										<Td>
											<Badge colorScheme={entry.status.colorScheme} variant="subtle" fontWeight="normal">
												{STATUS_LABELS[entry.status.key]}
											</Badge>
										</Td>
									</Tr>

									{expanded &&
										visibleRows.map((row, index) => {
											const bg = index % 2 === 0 ? propertyBg : stripeBg;
											return (
												<Tr key={row.propertyId || index} bg={bg}>
													<Td {...stickyOwner} bg={bg} />
													<Td {...stickyAddress} bg={bg} maxW="16rem">
														<Tooltip label={row.address}>
															<Link as={ReactRouterLink} to={`/properties/view/${row.propertyId}`}>
																<Text isTruncated>{row.shortAddress || row.address}</Text>
															</Link>
														</Tooltip>
													</Td>
													{["eur", "ron"].map((currency) => (
														<Td key={`rent-${currency}`} isNumeric>
															<Tooltip label={(row.rent.description || []).join("\n")} whiteSpace="pre-line">
																<Box as="span">
																	<Money value={row.rent.currency === currency ? row.rent.amount : 0} />
																</Box>
															</Tooltip>
														</Td>
													))}
													<Td fontSize="xs" color={muted}>
														{(row.rent.paymentMethod || []).join(", ")}
													</Td>
													{categories.flatMap((category) =>
														["eur", "ron"].map((currency) => (
															<Td key={`${category.key}-${currency}`} isNumeric>
																<Tooltip label={category.key === "other" ? (row.otherDescription || []).join("\n") : ""} whiteSpace="pre-line">
																	<Box as="span">
																		<Money value={row[category.key]?.[currency]} />
																	</Box>
																</Tooltip>
															</Td>
														)),
													)}
													{["eur", "ron"].map((currency) => (
														<Td key={`net-${currency}`} isNumeric>
															<Money value={row.rent.currency === currency ? row.subtotal?.[currency] : 0} emphasize />
														</Td>
													))}
													<Td>
														<Badge colorScheme={row.status.colorScheme} variant="subtle">
															{statusText(row)}
														</Badge>
													</Td>
												</Tr>
											);
										})}
								</Fragment>
							);
						})}
						{filteredOwners.length === 0 && (
							<Tr>
								<Td colSpan={7 + categories.length * 2} textAlign="center" py={8} color={muted}>
									No owners match this search.
								</Td>
							</Tr>
						)}
					</Tbody>
				</Table>
			</TableContainer>
		</Box>
	);
}
